import React, { useState, useEffect } from "react";
import { Menu, X, LayoutDashboard, ArrowRight, ChevronRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../store/hooks/useRedux";
import Logo from "./Logo";

/**
 * Header — floating site navigation.
 *
 * It is fixed above the page, so the page content scrolls underneath it.
 *  - overlay: the page's own hero runs behind the bar (the page adds its own
 *             top padding). Without it, a spacer keeps content clear of the bar.
 */
const Header = ({ overlay = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const navigate = useNavigate();

  // Get auth state from Redux
  const { isAuthenticated, user, logout } = useAuth();

  // Handle scroll effect with hide/show functionality
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Update scrolled state for styling
      setIsScrolled(currentScrollY > 20);

      // Header hide/show logic
      if (currentScrollY < 100) {
        // Always show header at top of page
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down - hide header
        setIsHeaderVisible(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show header
        setIsHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const navigationItems = [
    { label: "Features", href: "/#features" },
    { label: "For Vendors", href: "/#vendors" },
    { label: "How It Works", href: "/#how-it-works" },
    { label: "AI Planner", href: "/#ai-planner", isAI: true },
  ];

  const handleLogin = () => {
    navigate("/vendor-signin");
  };

  const handleVendorSignup = () => {
    navigate("/vendor-registration");
  };

  const handleDashboard = () => {
    navigate("/dashboard");
  };

  const handleLogout = async () => {
    try {
      await logout();
      setIsUserMenuOpen(false);
      navigate("/");
      console.log("User logged out successfully");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const LINK =
    "inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-purple-50 hover:text-gray-900 font-['Inter']";
  const CTA =
    "group inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-['Manrope'] font-semibold shadow-md shadow-purple-600/20 transition-all duration-300 hover:shadow-lg hover:shadow-purple-600/30";

  return (
    <>
      {/* Main Header — floating pill that hides on scroll down, returns on scroll up */}
      <nav
        aria-label="Main"
        className={`fixed inset-x-0 top-3 z-50 mx-auto max-w-[1180px] w-[calc(100%-1.5rem)] border backdrop-blur-xl transition-all duration-300
          ${isScrolled || isMenuOpen
            ? "border-gray-200/80 bg-white/90 shadow-xl shadow-purple-900/10"
            : "border-white/70 bg-white/60 shadow-lg shadow-purple-900/5"}
          ${isHeaderVisible ? "translate-y-0 opacity-100" : "-translate-y-[140%] opacity-0"}
          ${isMenuOpen ? "rounded-3xl" : "rounded-full"}
        `}
      >
        <div className="pl-3 pr-2.5 sm:pl-4 sm:pr-3">
          <div className="flex h-[4.25rem] items-center justify-between gap-3">
            {/* Logo */}
            <Logo
              size={36}
              className="flex-shrink-0 transition-transform duration-300 hover:scale-[1.03]"
              onClick={() => navigate("/")}
            />

            {/* Desktop navigation — links grouped in the centre */}
            <div className="hidden md:flex items-center gap-0.5 rounded-full border border-gray-200/60 bg-white/60 p-1">
              {navigationItems.map((item) => (
                <a key={item.label} href={item.href} className={LINK}>
                  {item.isAI && <Sparkles className="h-3.5 w-3.5 text-purple-600" />}
                  {item.label}
                </a>
              ))}
            </div>

            {/* Desktop actions */}
            <div className="hidden md:flex items-center gap-2 flex-shrink-0">
              {!isAuthenticated ? (
                <>
                  <button
                    onClick={handleLogin}
                    className="inline-flex h-11 items-center rounded-full border border-gray-200 bg-white px-5 text-sm font-semibold text-gray-900 transition-colors hover:border-purple-200 hover:bg-purple-50 font-['Manrope']"
                  >
                    Sign In
                  </button>
                  <button className={`${CTA} h-11 px-5 text-sm`} onClick={handleVendorSignup}>
                    Join Now
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </button>
                </>
              ) : (
                <button onClick={handleDashboard} className={`${CTA} h-11 px-5 text-sm`}>
                  <LayoutDashboard className="h-4 w-4" />
                  Dashboard
                </button>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
              className="md:hidden flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition-colors hover:border-purple-200 hover:bg-purple-50"
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {/* Mobile navigation */}
          {isMenuOpen && (
            <div className="md:hidden pb-4 pt-1 mobile-menu">
              <div className="flex flex-col gap-1 border-t border-gray-100 pt-3">
                {navigationItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="flex items-center justify-between rounded-2xl px-4 py-3.5 text-[15px] font-semibold text-gray-800 transition-colors hover:bg-purple-50 font-['Inter']"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="inline-flex items-center gap-2">
                      {item.isAI && <Sparkles className="h-4 w-4 text-purple-600" />}
                      {item.label}
                    </span>
                    <ChevronRight className="h-4 w-4 text-gray-300" />
                  </a>
                ))}

                <div className="mt-2 grid gap-2 border-t border-gray-100 pt-3">
                  {!isAuthenticated ? (
                    <>
                      <button
                        onClick={() => {
                          handleLogin();
                          setIsMenuOpen(false);
                        }}
                        className="h-12 rounded-2xl border border-gray-200 bg-white text-[15px] font-semibold text-gray-900 transition-colors hover:border-purple-200 hover:bg-purple-50 font-['Manrope']"
                      >
                        Sign In
                      </button>
                      <button
                        onClick={() => {
                          handleVendorSignup();
                          setIsMenuOpen(false);
                        }}
                        className={`${CTA} h-12 !rounded-2xl text-[15px]`}
                      >
                        Join Now
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        handleDashboard();
                        setIsMenuOpen(false);
                      }}
                      className={`${CTA} h-12 !rounded-2xl text-[15px]`}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      Dashboard
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Keeps page content clear of the floating bar */}
      {!overlay && <div aria-hidden="true" className="h-[5.75rem]" />}
    </>
  );
};

export default Header;
